import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  Email,
  EmailContact,
  EmailStatus,
  Events,
  PatientEmailsRepositoryService,
  WebSocketService
} from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientEmailsStore — sent + draft emails for the current patient.
 *
 * Auto-loads on patient change, owns the EmailEmailSent push
 * subscription, and exposes createDraft() which seeds a new email DTO
 * from the patient's email addresses, persists it via the repo, and
 * returns the new id for the caller to register with DraftsService.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientEmailsStore {
  private patientStore = inject(PatientStore);
  private repo = inject(PatientEmailsRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<Email[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  private currentChannel: string | null = null;
  private readonly eventName = Events.EmailEmailSent;
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.eventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        this.websocket.subscribe(channel, this.eventName, () => this.reload());
        this.currentChannel = channel;
      }
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Create a new email draft seeded from the current patient's primary
   * email address. Returns the new email id; caller registers via
   * DraftsService and reloads the list afterwards.
   */
  createDraft(): Observable<number> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientEmailsStore.createDraft() called with no current patient');
    }
    const toArray: string[] = [];
    if (patient.emailAddresses && patient.emailAddresses.length > 0) {
      toArray.push(patient.emailAddresses[0].emailAddress);
    }
    const mail = {
      patientId: patient.id,
      subject: '',
      to: toArray.map(address => ({ address, name: '' } as EmailContact)),
      cc: [],
      content: '',
      attachments: [],
      status: EmailStatus.DRAFT
    } as Email;
    return this.repo.create(mail).pipe(
      tap({
        next: () => this.reload()
      })
    );
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadAll(patientId).subscribe({
      next: emails => {
        this._items.set(emails);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient emails');
        
      }
    });
  }
}
